<template>
  <div
    class="map"
    ref="map"
  >
    <v-row
      class="map__buttons flex-column"
      dense
    >
      <v-col>
        <v-speed-dial
          v-model="fab"
          direction="right"
          class="map__btn"
        >
          <template v-slot:activator>
            <v-btn
              v-model="fab"
              class="btn--green"
              dark
              fab
              x-small
              :title="
            fab
              ? $i18n.t('dashboard.source.measurement.cancel')
              : !measureType
                ? $i18n.t('dashboard.source.measurement.sketchesAndMeasurements')
                : $i18n.t('dashboard.source.measurement.delete')
          "
              @click="clearMeasurement"
            >
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else-if="!fab && measureType">mdi-delete</v-icon>
              <v-icon v-else>mdi-ruler</v-icon>
            </v-btn>
          </template>
          <base-map-button
            title="dashboard.source.measurement.linearSketch"
            @click="addMeasurement('LineString')"
            icon="mdi-chart-line-variant"
          />
          <base-map-button
            title="dashboard.source.measurement.areaSketch"
            @click="addMeasurement('Polygon')"
            icon="mdi-ruler-square"
          />
        </v-speed-dial>
      </v-col>
      <v-col>
        <base-map-button
          title="dashboard.map.zoomToStart"
          @click="zoomToStartView"
          icon="mdi-fullscreen"
        />
      </v-col>

      <v-col>
        <base-map-button
          title="dashboard.map.legend"
          icon="mdi-map-legend"
          @click="openMapLegend"
        />
      </v-col>

      <slot name="map-buttons"></slot>
    </v-row>
    <map-address-search @search="searchAddress" @clear="clearSearchAddress"/>
    <MapLegendDialog
      :is-presentation="true"
      v-if="isLegendLoaded"
      ref="map_legend"
      :dialog="isLegendActive"
      :isDashboard="true"
      @hideLegend="isLegendActive = false"
    >
      <template v-slot:buttons>
        <v-card-actions>
          <div class="flex-grow-1" />
          <v-btn
            color="primary"
            text
            @click="isLegendActive = false"
          >{{ $i18n.t('dialog.confirmation.close') }}</v-btn>
        </v-card-actions>
      </template>
    </MapLegendDialog>
  </div>
</template>

<script>
import Map from 'ol/Map';
import View from 'ol/View';
import Tile from 'ol/layer/Tile';
import OSM from 'ol/source/OSM';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import Collection from 'ol/Collection';
import Select from 'ol/interaction/Select';
import { fromLonLat } from 'ol/proj';
import Heatmap from 'ol/layer/Heatmap';
import Point from 'ol/geom/Point';
import Draw from 'ol/interaction/Draw';
import { unByKey } from 'ol/Observable.js';
import { getWidth } from 'ol/extent';
import proj4 from 'proj4';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import TileGrid from 'ol/tilegrid/TileGrid';
import { Style, Stroke, Fill, Circle, Text } from 'ol/style';
import Overlay from 'ol/Overlay.js';
import { LineString, Polygon } from 'ol/geom.js';
import { getArea, getLength } from 'ol/sphere.js';
import { basemaps } from '@/models/Basemaps';
import { get as getProjection } from 'ol/proj';
import { getFeatureCentroid } from '@/utils/mapUtils';
import { defaults as defaultControls, FullScreen } from 'ol/control.js';
import { register } from 'ol/proj/proj4';
import { getWMSLayers } from '@/utils/mapUtils';
import searchAddressMixin from '@/mixins/searchAddressMixin';

// import { getWMSLayers } from '@/utils/mapUtils';

import vuetify from '@/plugins/vuetify.js';

const vuetifyColors = vuetify.preset.theme.themes.light;

export default {
  mixins: [searchAddressMixin],
  components: {
    MapLegendDialog: () => import('../dashboard/MapLegendDialog'),
    MapAddressSearch: () => import('@/components/MapAddressSearch')
  },
  props: {
    offers: {
      type: Array,
      required: true
    }
  },
  data: () => ({
    fab: false,
    map: undefined,
    isLegendActive: false,
    isLegendLoaded: false,
    measureTooltip: undefined,
    measureTooltipElement: undefined,
    measureType: undefined
  }),
  computed: {
    wmsLayers() {
      return this.$store.getters.getWMSLayers;
    },
    currentBasemap() {
      return this.$store.getters.getCurrentBasemap;
    }
  },
  watch: {
    currentBasemap(nV) {
      this.changeBaselayer(nV);
    },
    offers(data) {
      this.loadOffers(data);
    }
  },
  methods: {
    getLayerByName(name) {
      return this.map
        .getLayers()
        .getArray()
        .find(l => {
          return l.get('name') === name;
        });
    },
    loadOffers(data) {
      if (data.length > 0) {
        let features = [];
        for (let offer of data) {
          let feature = new GeoJSON().readFeature(offer.attributes.geometria, {
            featureProjection: 'EPSG:3857',
            dataProjection: 'EPSG:4326'
          });
          feature.set('id', offer.id);
          features.push(feature);
        }
        this.getLayerByName('offers')
          .getSource()
          .clear();
        this.getLayerByName('offers')
          .getSource()
          .addFeatures(features);
      }
    },
    selectOfferById(id) {
      const interaction = this.map
        .getInteractions()
        .getArray()
        .find(i => i.get('name') == 'selection');
      interaction.getFeatures().clear();
      if (id != undefined) {
        const feature = this.getLayerByName('offers')
          .getSource()
          .getFeatures()
          .find(f => f.get('id') == id);
        interaction.getFeatures().push(feature);
      }
    },
    zoomToExtent(extent) {
      this.map.getView().fit(extent, { maxZoom: 13, duration: 1000 });
    },
    zoomToFeature(feature) {
      this.map.getView().fit(feature.getGeometry().getExtent(), {
        maxZoom: 16,
        duration: 1000
      });
    },
    zoomToOfferById(oid, layer = 'offers') {
      const feature = this.getLayerByName(layer)
        .getSource()
        .getFeatures()
        .find(f => f.get('id') == oid);
      this.zoomToFeature(feature);
    },
    // getWMSLayers
    openMapLegend() {
      this.isLegendActive = true;
      this.isLegendLoaded = true;
    },
    formatArea(polygon) {
      var area = getArea(polygon);
      var output;
      if (area > 10000) {
        output = Math.round((area / 10000) * 100) / 100 + ' ' + 'ha';
      } else {
        output = Math.round((area / 10000) * 1000) / 1000 + ' ' + 'ha';
      }
      return output;
    },
    formatLength(line) {
      var length = getLength(line);
      var output;
      if (length > 100) {
        output = Math.round((length / 1000) * 100) / 100 + ' ' + 'km';
      } else {
        output = Math.round(length * 100) / 100 + ' ' + 'm';
      }
      return output;
    },
    addMeasurement(type) {
      const drawLayer = this.getLayerByName('measurement');
      this.getInteractionByName('selection').setActive(false); //block offers
      const drawInteraction = this.getInteractionByName('drawMeasurement');

      if (drawInteraction && this.measureType != type) {
        this.map.removeInteraction(drawInteraction);
        this.addMeasurementInteraction(drawLayer, type);
      } else {
        this.addMeasurementInteraction(drawLayer, type);
      }

      this.measureType = type;
      this.getInteractionByName('drawMeasurement').setActive(true);
      this.createMeasureTooltip();
    },
    createMeasureTooltip() {
      this.measureTooltipElement = document.createElement('div');
      this.measureTooltipElement.className = 'tooltip tooltip-measure';
      this.measureTooltip = new Overlay({
        element: this.measureTooltipElement,
        offset: [0, -15],
        positioning: 'bottom-center'
      });
      this.map.addOverlay(this.measureTooltip);
    },
    getInteractionByName(name) {
      return this.map
        .getInteractions()
        .getArray()
        .find(i => i.get('name') == name);
    },
    addMeasurementInteraction(layerName, type) {
      const drawInteraction = new Draw({
        source: layerName.getSource(),
        type: type,

        style: feature => {
          const geometry = feature.getGeometry();
          const styles = [];

          if (type === 'LineString' && geometry instanceof LineString) {
            geometry.forEachSegment((start, end) => {
              const line = new LineString([start, end]);
              styles.push(
                new Style({
                  geometry: line,
                  fill: new Fill({
                    color: `${vuetifyColors.accent}30`
                  }),
                  stroke: new Stroke({
                    color: 'rgba(0, 0, 0, 0.5)',
                    lineDash: [10, 10],
                    width: 2
                  }),
                  text: new Text({
                    text: this.formatLength(line),
                    font: '15px sans-serif',
                    fill: new Fill({ color: 'black' }),
                    placement: 'line',
                    stroke: new Stroke({
                      color: `${vuetifyColors.accent}30`,
                      width: 3
                    }),
                    offsetX: -20,
                    offsetY: 20
                  }),
                  image: new Circle({
                    radius: 5,
                    stroke: new Stroke({
                      color: vuetifyColors.accent
                    }),
                    fill: new Fill({
                      color: `${vuetifyColors.accent}50`
                    })
                  })
                })
              );
            });
          } else {
            styles.push(
              new Style({
                fill: new Fill({
                  color: `${vuetifyColors.accent}30`
                }),
                stroke: new Stroke({
                  color: 'rgba(0, 0, 0, 0.5)',
                  lineDash: [10, 10],
                  width: 2
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: `${vuetifyColors.accent}50`
                  })
                })
              })
            );
          }
          return styles;
        }
      });
      drawInteraction.set('name', 'drawMeasurement');
      this.map.addInteraction(drawInteraction);

      let listener;
      drawInteraction.on('drawstart', evt => {
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        // set sketch
        this.sketch = evt.feature;

        listener = this.sketch.getGeometry().on('change', evt => {
          if (evt.target instanceof Polygon) {
            const result = `${this.formatArea(
              evt.target
            )} <br> ${this.formatLength(evt.target)}`;
            this.measureTooltipElement.innerHTML = result;
            this.measureTooltip.setPosition(
              evt.target.getInteriorPoint().getCoordinates()
            );
          } else if (evt.target instanceof LineString) {
            this.measureTooltipElement.innerHTML = this.formatLength(
              evt.target
            );
            this.measureTooltip.setPosition(evt.target.getLastCoordinate());
          }
        });
      });

      drawInteraction.on('drawend', () => {
        this.measureTooltipElement.className = 'tooltip tooltip-static';
        this.measureTooltip.setOffset([0, -7]);
        // unset sketch
        this.sketch = null;
        unByKey(listener);
      });
    },
    clearMeasurement(e) {
      if (!this.fab && this.measureType) {
        e.stopPropagation();
        this.fab = false;
        this.measureType = false;
        this.getInteractionByName('drawMeasurement').setActive(false);
        this.getInteractionByName('selection').setActive(true);
        this.getLayerByName('measurement')
          .getSource()
          .clear();
        this.measureTooltipElement.parentNode.removeChild(
          this.measureTooltipElement
        ); //clear tooltip
      }
    },
    changeBaselayer(layerName) {
      for (let el of this.map.getLayers().getArray()) {
        if (el.get('group') === 'baselayers') {
          if (el.get('name') === layerName) {
            el.setVisible(true);
          } else {
            el.setVisible(false);
          }
        }
      }
    },
    zoomToStartView() {
      this.map.getView().setCenter([2229729.4005892696, 6903852.286423635]);
      this.map.getView().setZoom(6);
      this.$emit('clearSelection');
    }
  },
  mounted() {
    // Warstwa do miarki
    let measurementLayer = new VectorLayer({
      name: 'measurement',
      source: new VectorSource({}),
      style: feature => {
        const geometry = feature.getGeometry();
        const styles = [];

        if (geometry instanceof LineString) {
          geometry.forEachSegment((start, end) => {
            const line = new LineString([start, end]);
            styles.push(
              new Style({
                geometry: line,
                stroke: new Stroke({
                  color: vuetifyColors.accent,
                  width: 2
                }),
                fill: new Fill({
                  color: `${vuetifyColors.accent}30`
                }),
                text: new Text({
                  text: this.formatLength(line),
                  font: '15px sans-serif',
                  fill: new Fill({ color: 'black' }),
                  placement: 'line',
                  stroke: new Stroke({
                    color: `${vuetifyColors.accent}30`,
                    width: 3
                  }),
                  offsetX: -20,
                  offsetY: 20
                }),
                image: new Circle({
                  radius: 5,
                  stroke: new Stroke({
                    color: vuetifyColors.accent
                  }),
                  fill: new Fill({
                    color: `${vuetifyColors.accent}30`
                  })
                })
              })
            );
          });
        } else {
          styles.push(
            new Style({
              stroke: new Stroke({
                color: vuetifyColors.accent,
                width: 2
              }),
              fill: new Fill({
                color: `${vuetifyColors.accent}30`
              })
            })
          );
        }
        return styles;
      }
    });
    //    for (let basemap of basemaps) {
    //   this.map.addLayer(basemap);
    // }
    this.map = window.map = new Map({
      target: this.$el,
      view: new View({
        center: fromLonLat([20.03, 52.57]),
        zoom: 6
      }),
      layers: [
        new Tile({
          source: new OSM()
        }),
        ...basemaps,
        new VectorLayer({
          name: 'offers',
          source: new VectorSource({
            features: new Collection([]),
            wrapX: false
          }),
          style: new Style({
            image: new Circle({
              fill: new Fill({
                color: `${vuetifyColors.accent}20`
              }),
              stroke: new Stroke({
                color: vuetifyColors.accent,
                width: 2
              }),
              radius: 4
            }),
            fill: new Fill({
              color: `${vuetifyColors.accent}80`
            }),
            stroke: new Stroke({
              color: vuetifyColors.accent,
              width: 2
            })
          })
        })
      ]
    });

    let selectInteraction = new Select({
      style: new Style({
        image: new Circle({
          fill: new Fill({
            color: vuetifyColors.accent
          }),
          stroke: new Stroke({
            color: vuetifyColors.accent,
            width: 2
          }),
          radius: 4
        }),
        fill: new Fill({
          color: vuetifyColors.accent
        }),
        stroke: new Stroke({
          color: vuetifyColors.accent,
          width: 2
        })
      })
    });
    selectInteraction.set('name', 'selection');
    selectInteraction.on('select', e => {
      let id = undefined;
      if (e.selected.length > 0) {
        id = e.selected[0].get('id');
      }
      this.$emit('onSelectedId', id);
    });
    this.changeBaselayer(this.currentBasemap);
    this.map.addInteraction(selectInteraction);
    this.loadOffers(this.offers);
    this.map.addLayer(measurementLayer);
  }
};
</script>

<style lang="scss">
.ol-zoom {
  top: 2.5em;
}
.map {
  height: 100%;
  width: 100%;
  position: relative;
  .ol-control button {
    background-color: var(--v-primary-base);
  }
  .map__buttons {
    position: absolute;
    z-index: 5;
    left: 12px;
    top: 105px;
    max-height: calc(100% - 80px);
    .v-speed-dial__list {
      z-index: 51;
      padding: 0 2px;
    }
  }
}
.tooltip {
  position: relative;
  background: rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  color: white;
  padding: 4px 8px;
  opacity: 0.7;
  white-space: nowrap;
}
.tooltip-measure {
  opacity: 1;
  font-weight: bold;
}
.tooltip-static {
  background-color: var(--v-primary-base);
  color: whie;
  border: 1px solid white;
}
.tooltip-measure:before,
.tooltip-static:before {
  border-top: 6px solid rgba(0, 0, 0, 0.5);
  border-right: 6px solid transparent;
  border-left: 6px solid transparent;
  content: '';
  position: absolute;
  bottom: -6px;
  margin-left: -7px;
  left: 50%;
}
.tooltip-static:before {
  border-top-color: var(--v-primary-base);
}
</style>
